import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatTime, toPercent } from 'pano-utils';
import { useUpdateEffect } from 'react-use';
import type { KrpPlayer, SceneData } from '@types';
import limitNum from '@utils/limitNum';
import getPanoViewTrackList from '@utils/getPanoViewTrackList';
import ImgPause from './img-pause.png';
import ImgPlay from './img-play.png';
import ImgSpot from './img-spot.png';
import TrackSelect from './TrackSelect';

interface Props {
  videoPlaying: boolean;
  sceneData: SceneData;
  krpPlayer: KrpPlayer;
}

const Bar = styled.div`
  position: absolute;
  height: 2px;
  left: 0;
  right: 0;
  top: 7px;
`;
const Handler = styled.img.attrs({
  src: ImgSpot,
  draggable: false,
})`
  position: absolute;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  cursor: pointer;
`;

function VideoController({ videoPlaying, sceneData, krpPlayer }: Props) {
  const { id, transcodeTrack, cdnDomain, originTrack, config } = sceneData;
  const [totalTime, setTotalTime] = useState(0);
  const [curTime, setCurTime] = useState(0);
  const divRef = useRef<HTMLDivElement>(null);
  const posLeft = toPercent(curTime / totalTime);
  const timeStr = `${formatTime(curTime)}/${formatTime(totalTime)}`;
  const trackList = useMemo(
    () => getPanoViewTrackList({ transcodeTrack, cdnDomain, originTrack }),
    [transcodeTrack, cdnDomain, originTrack]
  );

  const items = useMemo(() => trackList.map((x) => x.name), []);
  const [track, setTrack] = useState(() => trackList[0]?.name ?? '');

  useEffect(() => {
    setTotalTime(krpPlayer?.videoTotalTime() ?? 0);
  }, [id]);

  useEffect(() => {
    if (videoPlaying) {
      const timer = setInterval(() => {
        const val = krpPlayer?.videoTime();
        if (val) {
          setCurTime(val);
        }
      }, 1000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [videoPlaying]);

  useUpdateEffect(() => {
    const trackItem =
      trackList.find((x) => x.resType === config.defaultResolution) ||
      trackList[0];
    handleTrackChange(trackItem?.name ?? '');
  }, [trackList, config.defaultResolution]);

  const handleMouseMove = (e: MouseEvent) => {
    if (!divRef.current) return;
    const { left, right, width } = divRef.current.getBoundingClientRect();
    const clientX = limitNum({ min: left, max: right, value: e.clientX });
    const ratio = (clientX - left) / width;
    const targetTime = totalTime * ratio;
    setCurTime(targetTime);
    krpPlayer?.seekVideo(targetTime);
  };

  const handleMouseUp = () => {
    krpPlayer?.playVideo();
    document.removeEventListener('mouseup', handleMouseUp, false);
    document.removeEventListener('mousemove', handleMouseMove, false);
  };

  const handleMouseDown = () => {
    krpPlayer?.pauseVideo();
    document.addEventListener('mouseup', handleMouseUp, false);
    document.addEventListener('mousemove', handleMouseMove, false);
  };

  const handleBarClick = (e: React.MouseEvent) => {
    if (!divRef.current) return;
    const { left, width } = divRef.current.getBoundingClientRect();
    const ratio = (e.clientX - left) / width;
    const targetTime = totalTime * ratio;
    setCurTime(targetTime);
    krpPlayer?.seekVideo(targetTime);
  };

  const handleTrackChange = (val: string) => {
    setTrack(val);
    const item = trackList.find((x) => x.name === val);
    if (item) {
      krpPlayer?.switchVideo(item.url);
    }
  };

  return (
    <div className="absolute bottom-85 flex h-50 w-300 items-center rounded bg-black-black bg-opacity-50 px-10">
      <img
        src={videoPlaying ? ImgPause : ImgPlay}
        className="mr-10 h-32 w-32 cursor-pointer"
        alt=""
        onClick={() => krpPlayer?.toggleVideo()}
      />
      <div className="mt-4 flex flex-1 flex-col px-5">
        <div
          className="relative mb-2 h-16 cursor-pointer"
          onClick={handleBarClick}
        >
          <Bar className="bg-blue" ref={divRef}>
            <div
              className="absolute top-0 right-0 bottom-0 bg-white"
              style={{ left: posLeft }}
            />
          </Bar>
          <Handler
            style={{ left: posLeft }}
            onMouseDown={handleMouseDown}
            onClick={(e) => e.stopPropagation()}
            src={ImgSpot}
            alt=""
          />
        </div>
        <div className="flex justify-between text-sm">
          <div>{timeStr}</div>
          <TrackSelect
            items={items}
            selected={track}
            onChange={handleTrackChange}
          />
        </div>
      </div>
    </div>
  );
}

export default VideoController;
